// src/utils/openAIMessageHistoryUtils.js

// Helper to get text from complex message content
function extractTextFromMessageContent(content) {
  if (typeof content === 'string') {
    return content;
  }
  if (Array.isArray(content)) {
    return content
      .filter(segment => segment.type === 'text')
      .map(segment => segment.text || '')
      .join('\n');
  }
  return '';
}

/**
 * Formats messages from DynamoDB for the OpenAI API.
 * @param {Array} dbMessages - Array of message items from DynamoDB, sorted by timestamp.
 *   Expected new DB message fields for OpenAI tool use:
 *   - Assistant tool request: { role: 'assistant', content: null | string, tool_calls: [], message_type: 'openai_tool_request' (or similar custom type) }
 *   - Tool result: { role: 'tool', tool_call_id: string, name: string, content: string (JSON result), message_type: 'openai_tool_result' (or similar custom type) }
 * @param {string} systemPrompt - The system prompt for the AI.
 * @returns {Array} - Formatted messages for OpenAI API.
 */
export function formatMessagesForOpenAI(dbMessages, systemPrompt) {
  const openAIMessages = [];

  // 1. System Prompt
  if (systemPrompt) {
    openAIMessages.push({ role: 'system', content: systemPrompt });
  }

  // 2. Process DB Messages
  for (const item of dbMessages) {
    // Skip meta messages entirely for the API call history
    if (item.message_type === 'meta' || item.meta === true) {
      continue;
    }

    let role = item.role;
    // Normalize 'user_attachment' role to 'user' for OpenAI
    if (role === 'user_attachment') {
      role = 'user';
    }

    // --- OpenAI Tool Use Handling ---
    // Check for assistant messages with tool_calls
    if (item.role === 'assistant' && item.tool_calls && Array.isArray(item.tool_calls) && item.tool_calls.length > 0) {
      openAIMessages.push({
        role: 'assistant',
        content: item.content || null, // Assistant might have text content before/with tool_calls. OpenAI expects null if only tool_calls.
        tool_calls: item.tool_calls.map(tc => ({ // Ensure structure matches OpenAI if it's not already exact
            id: tc.id,
            type: tc.type || 'function', // Default to function if type is missing
            function: {
                name: tc.function.name,
                arguments: tc.function.arguments
            }
        })),
      });
      continue; // Move to next message
    }

    // Check for tool role messages (results from your functions)
    // log item if item role is tool for debugging
    // This is for OpenAI tool results, which should have a tool_call_id and name
    if (item.role === 'tool') {
        console.log("Processing tool message:", item);
    }
  

    if (item.role === 'tool' && item.tool_call_id && typeof item.content === 'string') {
      // Parse the tool result to check if it contains base64 data
      let toolContent = item.content;
      try {
        const parsed = JSON.parse(item.content);
        // If it's a successful image generation, exclude base64 and keep only metadata
        if (parsed.success && parsed.imageCreated && parsed.s3Url) {
          toolContent = JSON.stringify({
            success: true,
            type: parsed.type,
            caption: parsed.caption,
            imageCreated: true
            // Exclude base64 data and s3Url from API calls
          });
        }
      } catch (e) {
        // If parsing fails, use original content
      }
      console.log("Pushing tool result to OpenAI messages:", {
        role: 'tool',
        tool_call_id: item.tool_call_id,
        name: item.name,
        content: toolContent
      });
      openAIMessages.push({
        role: 'tool',
        tool_call_id: item.tool_call_id,
        content: toolContent
      });
      continue;
    }
    // --- End OpenAI Tool Use Handling ---

    if (role === 'user' || role === 'assistant') {
      let textContent = '';

      // Handle different message types for content extraction
      if (item.message_type === 'text') {
        textContent = extractTextFromMessageContent(item.content);
      } else if (item.message_type === 'file') {
        // For file uploads, the 'content' field in DynamoDB is often the extracted text
        // or a description.
        textContent = item.content || `User uploaded file: ${item.fileName || 'unknown file'}`;
      } else if (item.message_type === 'drawing' && role === 'user') {
        textContent = item.imageInfo?.explanation || extractTextFromMessageContent(item.content) || "User provided a drawing.";
      } else if (item.message_type === 'drawing' && role === 'assistant') {
        // Assistant's description of a drawing (if applicable, or a textual response related to a drawing)
        textContent = extractTextFromMessageContent(item.content);
      } else if (item.message_type === 'image' && role === 'assistant') {
        // This was likely a Bedrock-specific artifact where the assistant "sent" an image.
        // For OpenAI, the assistant would describe the image or provide text leading to it.
        // We'll include a textual representation.
        textContent = item.imageInfo?.caption || item.imageInfo?.alt || `Assistant provided an image: graph`;
        if (typeof item.content === 'string' && item.content.trim() && !textContent.includes(item.content)) {
            textContent = `${item.content}. ${textContent}`;
        } else if (typeof item.content === 'string' && item.content.trim()) {
             textContent = item.content;
        }
      }
      // Skip old Bedrock tool_input/tool_result types if they somehow appear and aren't handled above
      else if (item.message_type === 'tool_input' || item.message_type === 'tool_result') {
        console.warn(`Skipping Bedrock-specific tool message in formatMessagesForOpenAI: type ${item.message_type}`);
        continue;
      }
      else {
        // Default for other types or if structure is unexpected
        textContent = extractTextFromMessageContent(item.content);
      }

      if (textContent && textContent.trim()) {
        openAIMessages.push({ role, content: textContent.trim() });
      } else if (role === 'assistant' && !textContent && !(item.tool_calls && item.tool_calls.length > 0)) {
        // If an assistant message has no text content and no tool_calls,
        // OpenAI API expects content to be a string (even if empty) or null.
        // If there were no tool_calls, send an empty string for content.
        openAIMessages.push({role, content: ""});
      }
    }
  }
  return openAIMessages;
}